import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchHome, fetchPredictionsBatch } from '@/api';
import MatchCard from '@/components/match/MatchCard';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { formatOdds, formatPercent, formatDateTime } from '@/lib/formatters';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, TrendingDown, ArrowUp, ArrowDown, ChevronRight,
  Target, Zap, BarChart3, Trophy, Star
} from 'lucide-react';

export default function HomePage() {
  const { t } = useTranslation();
  const { currentLang, getLocalizedPath } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState({});

  useEffect(() => {
    fetchHome().then(res => {
      setData(res.data);
      setLoading(false);
      const slugs = res.data.popular_matches?.map(m => m.slug).filter(Boolean) || [];
      if (slugs.length > 0) {
        fetchPredictionsBatch(slugs).then(r => setPredictions(r.data.predictions || {})).catch(() => {});
      }
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { popular_matches, value_bets, surebets, odds_movements, leagues, sports, bookmakers, stats } = data;

  const homeJsonLd = [
    {"@context": "https://schema.org", "@type": "WebSite", "name": "ODDSIS", "url": "https://oddsis.com",
      "potentialAction": {"@type": "SearchAction", "target": {"@type": "EntryPoint", "urlTemplate": `https://oddsis.com/${currentLang}/search?q={search_term_string}`}, "query-input": "required name=search_term_string"}},
    {"@context": "https://schema.org", "@type": "Organization", "name": "ODDSIS", "url": "https://oddsis.com",
      "description": "Professional odds comparison platform"}
  ];

  return (
    <div data-testid="home-page">
      <SEOHead
        title={t('home.seoTitle', 'Odds Comparison, Value Bets & Predictions')}
        description={t('home.seoDesc', `Compare odds across ${stats?.total_bookmakers || 30}+ bookmakers. Find value bets, surebets and AI predictions for ${stats?.total_matches || 200}+ matches.`)}
        jsonLd={homeJsonLd}
      />

      {/* Hero with ODDSIS stadium background */}
      <section className="relative overflow-hidden border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="/hero-bg.png" alt="" className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to bottom, rgba(2,6,23,0.82) 0%, rgba(2,6,23,0.72) 40%, rgba(2,6,23,0.92) 100%)'
          }} />
        </div>

        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-12 md:py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-white drop-shadow-lg" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('home.heroTitle1')}<br />
            <span className="text-emerald-400 drop-shadow-lg">{t('home.heroTitle2')}</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-200 max-w-xl drop-shadow">
            {t('home.heroDesc')}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <StatPill icon={<BarChart3 className="w-4 h-4" />} label={t('home.matches')} value={stats.total_matches} />
            <StatPill icon={<Target className="w-4 h-4" />} label={t('nav.valueBets')} value={stats.total_value_bets} color="amber" />
            <StatPill icon={<Zap className="w-4 h-4" />} label={t('nav.surebets')} value={stats.total_surebets} color="emerald" />
            <StatPill icon={<Star className="w-4 h-4" />} label={t('home.bookmarkersTitle')} value={stats.total_bookmakers} color="blue" />
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-7xl py-8 space-y-10">

        {/* Popular Matches */}
        <section data-testid="popular-matches">
          <SectionHeader title={t('home.popularMatches')} link={getLocalizedPath('football')} linkText={t('home.viewAll')} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {popular_matches?.slice(0, 6).map(match => (
              <MatchCard key={match.id} match={match} prediction={predictions[match.slug]} />
            ))}
          </div>
        </section>

        {/* Value Bets */}
        <section data-testid="value-bets-section">
          <SectionHeader title={t('home.topValueBets')} link={getLocalizedPath('valueBets')} linkText={t('home.viewAll')} icon={<Target className="w-5 h-5 text-amber-400" />} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {value_bets?.slice(0, 6).map(vb => (
              <Link key={vb.id} to={getLocalizedPath('match', vb.match_slug)}
                className="border border-amber-500/20 rounded-xl bg-slate-900/50 hover:bg-slate-900 hover:border-amber-500/40 transition-all p-4" data-testid={`vb-card-${vb.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                    {formatPercent(vb.value_percent)} {t('home.value')}
                  </Badge>
                  <div className="flex items-center gap-1.5">
                    {vb.league_logo && <img src={vb.league_logo} alt="" className="w-4 h-4 rounded object-contain" />}
                    <span className="text-xs text-slate-500">{vb.league_name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  {vb.home_team_logo && <img src={vb.home_team_logo} alt="" className="w-4 h-4 object-contain" />}
                  <p className="text-sm font-medium text-slate-200">{vb.home_team || vb.match_name?.split(' vs ')[0]}</p>
                  <span className="text-[10px] text-slate-600">vs</span>
                  {vb.away_team_logo && <img src={vb.away_team_logo} alt="" className="w-4 h-4 object-contain" />}
                  <p className="text-sm font-medium text-slate-200">{vb.away_team || vb.match_name?.split(' vs ')[1]}</p>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="text-slate-500">{vb.market_id} - {vb.selection}</span>
                  <span className="text-emerald-400 font-bold tabular-nums">{formatOdds(vb.best_odd)}</span>
                  <span className="text-slate-500">@ {vb.bookmaker_name}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Surebets */}
        <section data-testid="surebets-section">
          <SectionHeader title={t('home.topSurebets')} link={getLocalizedPath('surebets')} linkText={t('home.viewAll')} icon={<span className="text-xl">💰</span>} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {surebets?.slice(0, 6).map(sb => (
              <Link key={sb.id} to={getLocalizedPath('match', sb.match_slug)}
                className="border border-emerald-500/20 rounded-xl bg-slate-900/50 hover:bg-slate-900 hover:border-emerald-500/40 transition-all p-4" data-testid={`sb-card-${sb.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                    {formatPercent(sb.profit_percent)} {t('home.profit')}
                  </Badge>
                  <div className="flex items-center gap-1.5">
                    {sb.league_logo && <img src={sb.league_logo} alt="" className="w-4 h-4 rounded object-contain" />}
                    <span className="text-xs text-slate-500">{sb.league_name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  {sb.home_team_logo && <img src={sb.home_team_logo} alt="" className="w-4 h-4 object-contain" />}
                  <p className="text-sm font-medium text-slate-200">{sb.home_team || sb.match_name?.split(' vs ')[0]}</p>
                  <span className="text-[10px] text-slate-600">vs</span>
                  {sb.away_team_logo && <img src={sb.away_team_logo} alt="" className="w-4 h-4 object-contain" />}
                  <p className="text-sm font-medium text-slate-200">{sb.away_team || sb.match_name?.split(' vs ')[1]}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1">{sb.market_name}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {sb.selections?.map(s => (
                    <span key={s.selection} className="text-xs bg-slate-800 rounded px-2 py-0.5 text-slate-300">
                      {s.selection}: <span className="text-emerald-400 font-bold tabular-nums">{formatOdds(s.odds)}</span>
                      <span className="text-slate-500 ml-1">@ {s.bookmaker_name}</span>
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Odds Movements */}
        <section data-testid="odds-movements-section">
          <SectionHeader title={t('home.oddsMovements')} link={getLocalizedPath('oddsMovements')} linkText={t('home.viewAll')} icon={<TrendingUp className="w-5 h-5 text-blue-400" />} />
          <div className="border border-slate-800 rounded-xl overflow-hidden">
            {odds_movements?.slice(0, 8).map((m, i) => (
              <div key={m.id || i} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800/50 hover:bg-slate-900/50 transition-colors text-sm">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-slate-500 text-xs w-16 shrink-0">{m.market_id}</span>
                  <span className="text-slate-400 text-xs truncate">{m.bookmaker_id}</span>
                  <span className="text-slate-500 text-xs">{m.selection}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-slate-500 tabular-nums">{formatOdds(m.previous_odd)}</span>
                  {m.change_percent > 0
                    ? <ArrowUp className="w-3.5 h-3.5 text-emerald-500" />
                    : <ArrowDown className="w-3.5 h-3.5 text-red-500" />}
                  <span className={`tabular-nums font-medium ${m.change_percent > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatOdds(m.new_odd)}
                  </span>
                  <span className={`text-xs tabular-nums ${m.change_percent > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {formatPercent(m.change_percent)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Leagues + Bookmakers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <section data-testid="popular-leagues">
            <SectionHeader title={t('home.leagues')} />
            <div className="space-y-1">
              {leagues?.slice(0, 9).map(lg => (
                <Link key={lg.id} to={`/${currentLang}/${lg.sport_id}/${lg.slug}`}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-900/50 transition-colors group">
                  <div className="flex items-center gap-2">
                    {lg.logo_url ? (
                      <img src={lg.logo_url} alt="" className="w-5 h-5 rounded object-contain" loading="lazy" />
                    ) : (
                      <span className="text-xs text-slate-500 uppercase w-5 text-center">{lg.country_code}</span>
                    )}
                    <span className="text-sm text-slate-300 group-hover:text-slate-100">{lg.name}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400" />
                </Link>
              ))}
            </div>
          </section>

          <section data-testid="bookmakers-section">
            <SectionHeader title={t('home.bookmarkersTitle')} />
            <div className="grid grid-cols-2 gap-2">
              {bookmakers?.slice(0, 12).map(bkm => (
                <Link key={bkm.id} to={getLocalizedPath('bookmaker', bkm.slug)}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-900/30 rounded-lg border border-slate-800/50 hover:border-slate-700 transition-colors">
                  <div className="w-6 h-6 rounded bg-slate-800 flex items-center justify-center text-[10px] font-bold text-emerald-400">
                    {bkm.name[0]}
                  </div>
                  <span className="text-xs text-slate-400">{bkm.name}</span>
                  {bkm.featured && <Star className="w-3 h-3 text-amber-500 ml-auto" />}
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* World Cup 2026 Index Boost Section */}
        <section data-testid="wc-index-boost" className="bg-gradient-to-br from-slate-900 to-slate-950 border border-amber-500/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-bold tracking-tight uppercase text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              FIFA {currentLang === 'tr' ? 'Dünya Kupası' : 'World Cup'} 2026
            </h2>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            {currentLang === 'tr'
              ? 'ABD, Kanada ve Meksika\'da 48 takım, 104 maç. Tüm grupları, stadyumları ve şehir rehberlerini keşfet.'
              : '48 teams, 104 matches across USA, Canada and Mexico. Explore all groups, stadiums and city guides.'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {[
              { label: currentLang === 'tr' ? 'Dünya Kupası Hub' : 'World Cup Hub', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026' : 'world-cup-2026'}` },
              { label: currentLang === 'tr' ? 'Şehirler' : 'Cities', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/sehirler' : 'world-cup-2026/cities'}` },
              { label: currentLang === 'tr' ? 'Stadyumlar' : 'Stadiums', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/stadyumlar' : 'world-cup-2026/stadiums'}` },
              { label: currentLang === 'tr' ? 'ABD' : 'USA', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/ev-sahibi/abd' : 'world-cup-2026/host/usa'}` },
              { label: currentLang === 'tr' ? 'Kanada' : 'Canada', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/ev-sahibi/kanada' : 'world-cup-2026/host/canada'}` },
              { label: currentLang === 'tr' ? 'Meksika' : 'Mexico', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/ev-sahibi/meksika' : 'world-cup-2026/host/mexico'}` },
              { label: currentLang === 'tr' ? 'Gruplar' : 'Groups', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/gruplar' : 'world-cup-2026/groups'}` },
              { label: currentLang === 'tr' ? 'Fikstür' : 'Fixtures', path: `/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/skor-ve-fikstur' : 'world-cup-2026/scores'}` },
            ].map((link, i) => (
              <Link key={i} to={link.path}
                className="text-xs text-amber-400/80 hover:text-amber-300 bg-amber-500/5 border border-amber-500/10 hover:border-amber-500/30 rounded-lg px-3 py-2.5 transition-colors text-center">
                {link.label}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionHeader({ title, link, linkText, icon }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl md:text-2xl font-bold tracking-tight uppercase text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          {title}
        </h2>
      </div>
      {link && (
        <Link to={link} className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
          {linkText} <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

function StatPill({ icon, label, value, color = 'slate' }) {
  const colors = {
    slate: 'bg-slate-800/50 border-slate-700 text-slate-300',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${colors[color]}`}>
      {icon}
      <span className="font-bold tabular-nums">{value}</span>
      <span className="text-xs opacity-70">{label}</span>
    </div>
  );
}
